import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import styled from "styled-components";
import {
  PRIMARY_DARK_GREEN,
  PRIMARY_RED,
  SECONDARY_RED,
} from "../../constants/color";
import { CheckboxIcon } from "./icons/CheckboxIcon";
import { ErrorIcon } from "./icons/input/ErrorIcon";
import { CheckSomeIcon } from "./icons/CheckSomeIcon";

type CheckboxVariant = "filled" | "empty";

type Props = {
  name: string;
  variant: CheckboxVariant;
  checked?: boolean;
  label?: string;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  hasError?: boolean;
  errorMessage?: string;
  isSomeSelected?: boolean;
};

export const Checkbox: React.FC<Props> = ({
  name,
  label,
  checked,
  variant,
  disabled,
  onChange,
  hasError,
  errorMessage,
  isSomeSelected,
}) => {
  const formContext = useFormContext();
  const { setValue } = formContext ?? {};

  const [isChecked, setIsChecked] = useState<boolean>(checked ?? false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  useEffect(() => {
    setIsChecked(checked ?? false);
  }, [checked]);

  const handleOnClick = useCallback(() => {
    setIsChecked((prev) => !prev);
    onChange?.(!isChecked);

    setValue?.(name, !isChecked, { shouldValidate: true });
  }, [isChecked, onChange]);

  const fillColor = useMemo(() => {
    if (disabled) return "#e6e6e6";

    if (hasError) return SECONDARY_RED;

    if (isHovered) return PRIMARY_DARK_GREEN;

    if (isChecked) return PRIMARY_DARK_GREEN;

    return "#E6E6E6";
  }, [disabled, isChecked, isHovered, hasError]);

  return (
    <StyledCheckboxContainer
      $variant={variant}
      $isChecked={isChecked}
      $disabled={disabled}
      $hasError={hasError}
      onClick={handleOnClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchEnd={() => setIsHovered(false)}
    >
      <StyledCheckboxContent>
        {!isSomeSelected && <CheckboxIcon fillColor={fillColor} />}
        {isSomeSelected && <CheckSomeIcon fillColor={fillColor} />}

        <StyledLabel
          $variant={variant}
          $isChecked={isChecked}
          $isHovered={isHovered}
          $disabled={disabled}
          $hasError={hasError}
        >
          {label}
        </StyledLabel>
      </StyledCheckboxContent>

      {hasError && errorMessage && (
        <StyledErrorMessageContainer>
          <StyledErrorIconContainer>
            <ErrorIcon />
          </StyledErrorIconContainer>
          <StyledErrorMessage>{errorMessage}</StyledErrorMessage>
        </StyledErrorMessageContainer>
      )}
    </StyledCheckboxContainer>
  );
};

const StyledCheckboxContainer = styled.div<{
  $variant: CheckboxVariant;
  $isChecked: boolean;
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  width: fit-content;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 6px;
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  ${({ $variant, $isChecked, $disabled, $hasError }) =>
    $variant === "filled" &&
    `
      padding: 14px 20px;
      border-radius: 8px;
      border: 1px solid ${
        $disabled
          ? "#CCCCCC"
          : $hasError
            ? PRIMARY_RED
            : $isChecked
              ? PRIMARY_DARK_GREEN
              : "#CCCCCC"
      };
      background-color: ${$disabled ? "#F2F2F2" : "#FFFFFF"};

      &:hover {
        border: 1px solid ${
          $disabled ? "#CCCCCC" : $hasError ? PRIMARY_RED : PRIMARY_DARK_GREEN
        };
      }
     
  `}
`;

const StyledLabel = styled.div<{
  $variant: CheckboxVariant;
  $isChecked: boolean;
  $isHovered: boolean;
  $disabled?: boolean;
  $hasError?: boolean;
}>`
  color: ${({ $variant, $isChecked, $isHovered, $disabled, $hasError }) =>
    $disabled
      ? "#999999"
      : $hasError
        ? $variant === "filled"
          ? PRIMARY_RED
          : "#333333"
        : $isChecked || $isHovered
          ? PRIMARY_DARK_GREEN
          : "#333333"};
  font-size: 14px;
  font-weight: ${({ $variant }) => ($variant === "filled" ? 700 : 400)};
  line-height: 100%;
`;

const StyledCheckboxContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
`;

const StyledErrorMessageContainer = styled.div`
  display: flex;
  align-items: flex-end;
`;

const StyledErrorIconContainer = styled.div`
  margin: 3px;
`;

const StyledErrorMessage = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: ${PRIMARY_RED};
`;
